import React from "react";
import { PublishStatus } from "@graffer-inc/cloud-gov-core";
import { ProcedureEntity } from "../../../../application/domain/procedures/procedure-entity";
import { SmartApplyR } from "../route";
import {
  IconKeys,
  PrimaryButton,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";
import { RBACButton } from "../../../molecules/rbac-button";
import { toast } from "react-toastify";

export type ProcedureDetailButtonProps = {
  procedure?: ProcedureEntity;
  onPublishSettingModalOpen: () => void;
  onReturnToDraft: () => void;
  onResumeAcceptModalOpen: () => void;
  canShowPreview: boolean;
  onOpenPreview: () => void;
};

class ProcedureDetailButtonEntity {
  props: Required<ProcedureDetailButtonProps>;
  constructor(props: Required<ProcedureDetailButtonProps>) {
    this.props = props;
  }

  getButtons(): {
    [key in PublishStatus]: React.ReactNode[];
  } {
    const confirmProcedureButtonProps = {
      icon: "OpenInNewIcon" as IconKeys,
      text: "申請を確認",
      onClick: () => {
        window.open(
          `${SmartApplyR.Applications.link({})}?procedureId=` +
            this.props.procedure.id,
          "_blank",
        );
      },
    };
    const openPreviewButtonProps = {
      icon: "OpenInNewIcon" as IconKeys,
      text: "申請プレビュー",
      onClick: () => this.props.onOpenPreview(),
      disabled: !!this.props.procedure.unPublishableError(), // 公開できない手続きは fe-portal で正しく表示できない可能性があるため、制御する
    };
    return {
      DRAFT: [
        this.props.canShowPreview && (
          <SecondaryButton {...openPreviewButtonProps} />
        ),
        <RBACButton
          key={"draftRBACButton"}
          target={this.props.procedure}
          requirePermissions={"procedures.publish"}
          onClick={() => {
            const msg = this.props.procedure.unPublishableError();
            if (!msg) {
              this.props.onPublishSettingModalOpen();
            } else {
              toast.error(msg);
            }
          }}
          buttonTypes={"primary"}
          buttonText={"手続きを公開"}
        />,
      ],
      BEFORE_PUBLISH: [
        this.props.canShowPreview && (
          <SecondaryButton {...openPreviewButtonProps} />
        ),
        <RBACButton
          key={"beforePublishRBACButton"}
          target={this.props.procedure}
          requirePermissions={"procedures.publish"}
          onClick={this.props.onReturnToDraft}
          buttonTypes={"primary"}
          buttonText={"下書きに戻す"}
        />,
      ],
      BEFORE_ACCEPTING: [
        this.props.canShowPreview && (
          <SecondaryButton {...openPreviewButtonProps} />
        ),
        <PrimaryButton
          key={"beforeAcceptingPrimaryButton"}
          {...confirmProcedureButtonProps}
        />,
      ],
      ACCEPTING: [
        this.props.canShowPreview && (
          <SecondaryButton {...openPreviewButtonProps} />
        ),
        <PrimaryButton
          key={"acceptingPrimaryButton"}
          {...confirmProcedureButtonProps}
        />,
      ],
      PAUSING_ACCEPT: [
        this.props.canShowPreview && (
          <SecondaryButton {...openPreviewButtonProps} />
        ),
        <SecondaryButton
          key={"pausingAcceptSecondaryButton"}
          {...confirmProcedureButtonProps}
        />,
        <RBACButton
          key={"pausingAcceptRBACButton"}
          target={this.props.procedure}
          requirePermissions={"procedures.publish"}
          onClick={this.props.onResumeAcceptModalOpen}
          buttonTypes={"primary"}
          buttonText={"受付を再開"}
        />,
      ],
      FINISHED_ACCEPTING: [
        this.props.canShowPreview && (
          <SecondaryButton {...openPreviewButtonProps} />
        ),
        <PrimaryButton
          key={"finishedAcceptingPrimaryButton"}
          {...confirmProcedureButtonProps}
        />,
      ],
      UNPUBLISHED: [
        this.props.canShowPreview && (
          <SecondaryButton {...openPreviewButtonProps} />
        ),
        <PrimaryButton
          key={"unpublishedPrimaryButton"}
          {...confirmProcedureButtonProps}
        />,
      ],
    };
  }
}

export function useButtons(props: ProcedureDetailButtonProps) {
  if (!props.procedure) return [];
  if (props.procedure.isArchived()) return [];
  const entity = new ProcedureDetailButtonEntity({
    ...props,
    procedure: props.procedure,
  });

  return entity.getButtons()[props.procedure.publishStatus];
}
